import { RefObject, useEffect, useState } from 'react';

const DEFAULT_HEIGHT = 400;
const MIN_HEIGHT = 200;

/**
 * Hook that measures the available height from an element's top position to the bottom of the viewport.
 * Useful for grids that need to fill remaining vertical space without hardcoded calc() offsets.
 *
 * @param ref - Reference to the container element
 * @param bottomOffset - Additional offset from the bottom (e.g., for padding/margins below the element)
 * @returns The calculated available height in pixels (defaults to 400px before measurement)
 */
export function useAvailableHeight(ref: RefObject<HTMLElement>, bottomOffset = 24): number {
  const [height, setHeight] = useState<number>(DEFAULT_HEIGHT);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateHeight = () => {
      const rect = element.getBoundingClientRect();
      const available = window.innerHeight - rect.top - bottomOffset;
      setHeight(Math.max(available, MIN_HEIGHT));
    };

    updateHeight();
    // Re-measure after layout settles (handles tab switches, animations)
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(updateHeight);
    });

    // Re-measure when the element's size changes (e.g., toolbar renders, content shifts)
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);

    window.addEventListener('resize', updateHeight);
    window.addEventListener('orientationchange', updateHeight);

    return () => {
      cancelAnimationFrame(raf1);
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('orientationchange', updateHeight);
    };
  }, [ref, bottomOffset]);

  return height;
}
